import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { FaEllipsisV, FaEdit, FaTrash } from "react-icons/fa";
import { Dropdown, Button, Form, Modal } from "react-bootstrap";
import Avatar from "../../components/notes/Avatar";
import TimeAgo from "../../components/timeAgo/TimeAgo";

const PropertyComment = ({ Id, ownerId, commentType, allComments, addComment, editComment, deleteComment }) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const currentUserId = loggedInUser?.user?.id || loggedInUser?.id || loggedInUser?.userDetails?.id;
  const isOwner = Boolean(ownerId && Number(currentUserId) === Number(ownerId));
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyText, setReplyText] = useState({});
  const [activeReplyId, setActiveReplyId] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [updatedComment, setUpdatedComment] = useState("");
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal state for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState(null);
  const [isReplyDelete, setIsReplyDelete] = useState(false);

  const userId = currentUserId;
  const token = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetUrl = process.env.REACT_APP_ASSET_URL || baseUrl || "";

  const fetchCommentsUrl = `${baseUrl}${allComments}/${userId}/${Id}`;
  const addCommentUrl = `${baseUrl}${addComment}`;
  const editCommentUrl = `${baseUrl}${editComment}`;

// Filter logic for visibility
const filterCommentsForUser = (allComments) => {
  if (isOwner) {
    // Owner sees everything
    return allComments;
  }

  return allComments
    .map((comment) => {
      if (comment.user?.id === ownerId) {
        // Everyone can see owner's comments, but not replies
        return { ...comment, children: [] };
      }

      if (comment.user?.id === currentUserId) {
        // User sees their own comments and replies
        return comment;
      }

      // If not the owner or current user, hide the comment entirely
      return null;
    })
    .filter(Boolean);
};

  useEffect(() => {
    fetchComments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchComments = async () => {
    try {
      const response = await axios.post(
        fetchCommentsUrl,
        {
          page_number: 1,
          page_size: 200,
          order_by: "DESC",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.statusType === "SUCCESS" || Array.isArray(response.data[commentType])) {
        setComments(response.data[commentType] || response.data.comments || []);
      } else {
        throw new Error("Failed to fetch comments");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        comment: newComment,
        user: { id: userId },
        desired_request: { id: Id },
      };
      const response = await axios.post(
        addCommentUrl,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.statusType === "SUCCESS") {
        setNewComment("");
        fetchComments();
      } else {
        throw new Error("Failed to add comment");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId) => {
    setSubmitting(true);
    try {
      const response = await axios.put(
        editCommentUrl,
        {
          id: commentId,
          comment: updatedComment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.statusType === "SUCCESS") {
        setEditingComment(null);
        setUpdatedComment("");
        fetchComments();
      } else {
        throw new Error("Failed to edit comment");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Open modal for comment delete
  const confirmDeleteComment = (commentId) => {
    setCommentIdToDelete(commentId);
    setIsReplyDelete(false);
    setShowDeleteModal(true);
  };

  // Open modal for reply delete
  const confirmDeleteReply = (replyId) => {
    setCommentIdToDelete(replyId);
    setIsReplyDelete(true);
    setShowDeleteModal(true);
  };

  // Delete comment or reply after confirmation
  const handleDeleteConfirmed = async () => {
    if (!commentIdToDelete) return;
    try {
      const deleteUrl = `${baseUrl}${deleteComment}${commentIdToDelete}`;
 
      const response = await axios.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.data?.statusType === "SUCCESS") {
        await fetchComments();
      } else {
        throw new Error("Failed to delete comment");
      }
    } catch (err) {
      setError("Failed to delete comment!");
    } finally {
      setShowDeleteModal(false);
      setCommentIdToDelete(null);
      setIsReplyDelete(false);
    }
  };

  const handleAddReply = async (parentId) => {
    if (!replyText[parentId]?.trim()) return;
    setSubmitting(true);
    try {
      const response = await axios.post(
        addCommentUrl,
        {
          parent_id: parentId,
          comment: replyText[parentId],
          user: { id: userId },
          desired_request: { id: Id },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.statusType === "SUCCESS") {
        setReplyText((prev) => ({ ...prev, [parentId]: "" }));
        setActiveReplyId(null);
        fetchComments();
      } else {
        throw new Error("Failed to add reply");
      }
    } catch (err) {
      setError("Failed to add reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditReply = async (replyId) => {
    setSubmitting(true);
    try {
      const response = await axios.put(
        editCommentUrl,
        {
          id: replyId,
          comment: updatedComment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.statusType === "SUCCESS") {
        setEditingComment(null);
        setUpdatedComment("");
        await fetchComments();
      } else {
        throw new Error("Failed to edit reply");
      }
    } catch (err) {
      console.error("Error editing reply:", err);
      setError("Failed to edit reply");
    } finally { 
      setSubmitting(false);
    }
  };
  if (loading) return <p>Loading comments...</p>;
  if (error) return <p>Error: {error}</p>;

  const renderReplies = (replies, level = 1) => (
      <ul style={{ marginLeft: `${level * 20}px`, paddingLeft: "10px" }}>
        {replies.map((reply) => (
          <li key={reply.id} className="mb-3 position-relative">
            <div className="d-flex align-items-start mb-3">
              <div className="w-10">
                
                <Avatar icon={reply.user.first_name} src={assetUrl + reply.user.photo} size={25} />
              </div>
              <div className="flex-grow-1"> 
                <strong>
                  {reply.user?.first_name} {reply.user?.last_name}:
                </strong>
                <TimeAgo timestamp={reply.created_on}  className="text-muted ms-2"/>
                {editingComment === reply.id ? (
                  <div className="d-flex gap-2 mt-2">
                    <input
                      type="text"
                      value={updatedComment}
                      onChange={(e) => setUpdatedComment(e.target.value)}
                      className="form-control w-75"
                    />
                    <Button onClick={() => handleEditReply(editingComment)} disabled={submitting}>
                      {submitting ? "Saving..." : "Save"}
                    </Button>
                  </div>
                ) : (
                  <p>{reply.comment}</p>
                )}
                {reply.user?.id !== userId && (
                  <button
                    onClick={() => setActiveReplyId(activeReplyId === reply.id ? null : reply.id)}
                    className="btn btn-primary p-1 mt-1"
                  >
                    Reply
                  </button>
                )}
                {activeReplyId === reply.id && (
                  <div className="mt-2 w-100">
                    <Form.Control
                      type="text"
                      value={replyText[reply.id] || ""}
                      onChange={(e) => setReplyText({ ...replyText, [reply.id]: e.target.value })}
                      placeholder="Write a reply..."
                    />
                    <Button
                      onClick={() => handleAddReply(reply.id)} disabled={submitting}
                      variant="primary"
                      className="mt-2"
                    >
                      {submitting ? "Replying..." : "Reply"}
                    </Button>
                  </div>
                )}

              </div>
              {reply.user?.id === userId && (
                <Dropdown>
                  <Dropdown.Toggle variant="light" size="sm" id={`dropdown-${reply.id}`}>
                    <FaEllipsisV />
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item onClick={() => {
                      setEditingComment(reply.id);
                      setUpdatedComment(reply.comment);
                    }}>
                      <FaEdit className="me-2 text-primary" /> Edit
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => confirmDeleteReply(reply.id)} className="text-danger">
                      <FaTrash className="me-2" /> Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
            {reply.children && reply.children.length > 0 && renderReplies(reply.children, level + 1)}
          </li>
        ))}
      </ul>
  );

  return (
    <div className="container mt-4">
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this {isReplyDelete ? "reply" : "comment"}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <h3 className="d-flex align-items-center mb-3">
        Comments 
      </h3>
      {!isOwner ? (
        <div className="d-flex gap-2 mb-3">
          <input
            type="text"
            value={newComment}
            className="form-control"
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
          />
          <button onClick={handleAddComment} disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? "Adding..." : "Add"}
          </button>
        </div>
      ) : (
        <div className="alert alert-light border text-secondary py-2 px-3 mb-3 small fw-medium">
          As the post author, you can reply directly to user comments below.
        </div>
      )}
      <ul className="list-unstyled mt-3">
        {/* {comments.map((comment) => ( */}
        {filterCommentsForUser(comments).map((comment) => (

          <li key={comment.id} className="mb-3 position-relative">
            <div className="d-flex align-items-start mb-3">
              <div className="w-10">
                <Avatar icon={comment.user.first_name} src={assetUrl + comment.user.photo} size={25} />
              </div>
              <div className="flex-grow-1">
                <strong> 
                  {comment.user?.first_name} {comment.user?.last_name}:
                </strong>
                <TimeAgo timestamp={comment.created_on}  className="text-muted ms-2"/>
                <br />
                {editingComment === comment.id ? (
                  <div className="d-flex gap-2 mt-2">
                    <input
                      type="text"
                      value={updatedComment}
                      onChange={(e) => setUpdatedComment(e.target.value)}
                      className="form-control w-75"
                    />
                    <button onClick={() => handleEditComment(comment.id)} disabled={submitting}
                      className="btn btn-primary"
                    >
                      {submitting ? "Saving..." : "Submit"}
                    </button>
                  </div>
                ) : (
                  <p>{comment.comment}</p>
                )}
                {comment.user?.id !== userId && (
                  <button
                    onClick={() => setActiveReplyId(activeReplyId === comment.id ? null : comment.id)}
                    className="btn btn-primary p-1 mt-1 "
                  >
                    Reply
                  </button>
                )}
              </div>
              {comment.user?.id === userId && (
                <Dropdown>
                  <Dropdown.Toggle variant="light" size="sm" id={`dropdown-${comment.id}`}>
                    <FaEllipsisV />
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item onClick={() => {
                      setEditingComment(comment.id);
                      setUpdatedComment(comment.comment);
                    }}>
                      <FaEdit className="me-2 text-primary" /> Edit
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => confirmDeleteComment(comment.id)} className="text-danger">
                      <FaTrash className="me-2" /> Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
            {activeReplyId === comment.id && (
              <div className="mt-2 w-100">
                <Form.Control
                  type="text"
                  value={replyText[comment.id] || ""}
                  onChange={(e) => setReplyText({ ...replyText, [comment.id]: e.target.value })}
                  placeholder="Write a reply..."
                />
                <Button
                  onClick={() => handleAddReply(comment.id)} disabled={submitting}
                  variant="primary"
                  className="mt-2"
                >
                  {submitting ? "Replying..." : "Reply"}
                </Button>
              </div>
            )}
            <div style={{ marginLeft: "50px", marginTop: "10px", listStyle: "none", padding: 0 }}>
              {Array.isArray(comment.children) && comment.children.length > 0 && renderReplies(comment.children)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PropertyComment;